import {Dimensions, ScrollView, StyleSheet} from 'react-native';
import React, {useCallback, useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '@buncha/config/redux/hooks';

import {Box} from '../../../src/components/core/view';
import DeliveryButtonComponent from '../components/orderDetails/deliveryButtonComponent';
import {DeliveryLocationStatus} from '../types/common';
import Header from '../../../src/components/composites/Header/Header';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../../../src/components/conditional/If';
import MapComponent from '../components/orderDetails/mapComponent';
import {Modules} from '@buncha/utils/types';
import NameAndAddressDetails from '../components/common/nameAndAddressDetails';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import OrderInfo from '../components/orderDetails/orderInfo';
import ScreenHeader from '../../common/components/screenHeader';
import {SpinnerComponent} from '../../../src/components/core/loader';
import {homeActions} from '@buncha/home/reducer';
import listener from '../../../src/config/listener';
import {navigation} from '@buncha/config/navigation';
import onfleetService from '@buncha/config/onfleet';
import {useColorThemeType} from '../../../src/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useGetOrdersDetails} from '../hooks/orderDetails';
import {useIsFocused} from '@react-navigation/native';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

type Props = NativeStackScreenProps<any, any>;

const OrderDetails: React.FC<Props> = props => {
  const taskId = String(props?.route?.params?.taskId) || '';
  const driverTasks = useAppSelector(gState => gState.home.driverTasks);
  const isFocused = useIsFocused();
  const {title} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const globalTask = driverTasks?.find(t => t.id === taskId);
  const isFirstItem = driverTasks?.[0]?.id === taskId;

  const [task, setTask] = useState(globalTask);

  const orderId =
    String(props?.route?.params?.orderId ?? task?.metadata?.orderId) || '';
  const runId =
    Number(props?.route?.params?.runId ?? task?.metadata?.runId) || '';

  const theme = useColorThemeType('main');
  const {getOrderDetails, loading, orderData} = useGetOrdersDetails();
  const appDispatch = useAppDispatch();
  const buyerInfo = orderData?.buyer;
  const buyerAddress = orderData?.deliveryLocation?.userAddress;
  const deliveryInstruction =
    orderData?.deliveryInstruction || buyerAddress?.deliveryInstruction || '';
  const deliveryLocation = orderData?.deliveryLocation;
  const deliveryNote = deliveryLocation?.deliveryNote;
  const runInfo = orderData?.run;
  const orderItems = orderData?.itemsForDriver || [];
  const storeId = orderData?.run?.store?.id;
  const isB2BStore = useIsMeijerStore(storeId);

  const lat =
    task?.destinationAddress?.locationLatitude ||
    deliveryLocation?.userAddress?.lat ||
    0;
  const lon =
    task?.destinationAddress?.locationLongitude ||
    deliveryLocation?.userAddress?.lon ||
    0;

  const getOrderInfo = useCallback(() => {
    if (Number(orderId))
      getOrderDetails(Number(orderId).toString(), runId || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, runId]);

  useEffect(() => {
    onfleetService.fetchActiveTask().then(response => {
      appDispatch(homeActions.setActiveTask(response));
    });
    const unsubscribe = listener.addListener('onTheWay', getOrderInfo);
    getOrderInfo();
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isB2BStore]);

  useEffect(() => {
    setTimeout(() => {
      setTask(globalTask);
    }, 2000);
  }, [globalTask]);

  useEffect(() => {
    if (
      !task &&
      isFocused &&
      orderData &&
      deliveryLocation?.deliveryStatus !== DeliveryLocationStatus.delivered &&
      deliveryLocation?.deliveryStatus !== DeliveryLocationStatus.deliveryFailed
    )
      navigation.pop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, orderData]);

  return (
    <Header
      loading={loading}
      headerColor={theme?.background?.blue?.[300]}
      showDefaultLeftHeader>
      <ScreenHeader
        title={
          task?.shortId ? `${title?.task}${task?.shortId}` : title?.orderDetails
        }
      />
      <If condition={loading}>
        <SpinnerComponent containerStyle={styles.spinnerContainer} />
      </If>
      <If condition={!loading}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <MapComponent lat={lat} lon={lon} />
          <Box style={styles.parentBox}>
            <NameAndAddressDetails
              buyerAddress={buyerAddress}
              deliveryInstructions={deliveryInstruction}
              deliveryNote={deliveryNote}
              buyerInfo={buyerInfo}
              forOrderDetails={true}
              orderId={Number(orderId)}
              runId={runInfo?.id}
              conversationId={orderData?.buyerChatInfo?.conversationId}
              storeId={orderData?.run?.store?.id}
              currentTask={task}
              bagsUsed={orderData?.bagsUsed}
            />
            <OrderInfo runInfo={runInfo} orderItems={orderItems} />
          </Box>
        </ScrollView>
        <If condition={task}>
          <DeliveryButtonComponent
            deliveryLocation={deliveryLocation}
            orderId={orderId}
            storeId={orderData?.run?.store?.id ?? 0}
            isActive={isFirstItem}
            task={task}
          />
        </If>
      </If>
    </Header>
  );
};

export default OrderDetails;

const styles = StyleSheet.create({
  parentBox: {
    paddingHorizontal: 20,
  },
  webHeader: {
    padding: 30,
  },
  spinnerContainer: {
    justifyContent: 'center',
    height: Dimensions?.get('window').height * 0.8,
  },
});
